import React, { useState, useRef } from 'react';

const UserDashboard = ({ requests, onUpdateRequest }) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  
  // Cancel Confirmation State
  const [requestToCancel, setRequestToCancel] = useState(null);
  
  // Feedback State
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  
  // File Upload State
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleManageClick = (req) => {
    setSelectedRequest(req);
    setRating(5);
    setComment('');
    setIsManageModalOpen(true);
  };

  const handleCancelClick = (req, e) => {
    e.stopPropagation();
    setRequestToCancel(req);
  };

  const confirmCancelRequest = () => {
    if (!requestToCancel) return;
    
    const updated = {
      ...requestToCancel,
      status: 'CANCELLED'
    };
    
    onUpdateRequest(updated);
    
    // If the cancelled request was currently open in the manage modal, update it
    if (selectedRequest && selectedRequest.id === requestToCancel.id) {
      setSelectedRequest(updated);
    }
    
    setRequestToCancel(null);
  };

  const handleMarkCompleted = () => {
    if (!selectedRequest) return;
    const updated = { ...selectedRequest, status: 'COMPLETED' };
    onUpdateRequest(updated);
    setSelectedRequest(updated); // Update local modal state
  };

  const handleSubmitFeedback = (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    const feedback = {
      rating,
      comment,
      timestamp: new Date().toISOString()
    };

    const updated = { ...selectedRequest, feedback };
    onUpdateRequest(updated);
    setSelectedRequest(updated);
    // Optionally close modal here, or keep open to see result
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || !selectedRequest) return;

    setUploading(true);
    
    const newDocs = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      
      await new Promise((resolve) => {
        reader.onload = (event) => {
          if (event.target?.result) {
            newDocs.push(event.target.result);
          }
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    const updatedDocs = [...(selectedRequest.completionDocs || []), ...newDocs];
    const updated = { ...selectedRequest, completionDocs: updatedDocs };
    
    onUpdateRequest(updated);
    setSelectedRequest(updated);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">My Requests</h1>
        <p className="text-slate-500 dark:text-slate-400">Manage your service requests, offers, and history.</p>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-dashed">
          <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No requests yet</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">Post your first request to get started with OpenRequests.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {requests.map((req) => (
            <div key={req.id} className={`bg-white dark:bg-slate-800 rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow ${req.status === 'CANCELLED' ? 'border-slate-100 dark:border-slate-700 opacity-75' : 'border-slate-200 dark:border-slate-700'}`}>
              <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className={`text-xl font-bold ${req.status === 'CANCELLED' ? 'text-slate-500 dark:text-slate-500 line-through' : 'text-slate-900 dark:text-white'}`}>{req.title || "Service Request"}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        req.status === 'OPEN' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' :
                        req.status === 'IN_PROGRESS' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' :
                        req.status === 'COMPLETED' ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' :
                        'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                      }`}>
                        {req.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                        <span>Posted on {new Date(req.postedAt).toLocaleDateString()}</span>
                        {req.location && (
                            <>
                                <span>•</span>
                                <span>{req.location}</span>
                            </>
                        )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {req.status === 'OPEN' && (
                      <button
                        onClick={(e) => handleCancelClick(req, e)}
                        className="px-4 py-2 bg-white dark:bg-slate-700 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm shadow-sm"
                      >
                        Cancel
                      </button>
                    )}
                    <button 
                      onClick={() => handleManageClick(req)}
                      className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors text-sm shadow-sm"
                    >
                      Manage Request
                    </button>
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 mb-4">
                  <p className="text-slate-700 dark:text-slate-300 mb-3">{req.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                     <span className="flex items-center gap-1">
                       <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                       {req.timeframe === 'asap' ? 'ASAP' : req.timeframe}
                     </span>
                     <span className="flex items-center gap-1">
                       <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                       Max {req.maxResponses} Responses
                     </span>
                     {req.category && (
                         <span className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded text-indigo-600 dark:text-indigo-400 font-medium text-xs">
                           {req.category}
                         </span>
                     )}
                     {req.attachments && req.attachments.length > 0 && (
                       <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-medium">
                         <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                         {req.attachments.length} Attachment(s)
                       </span>
                     )}
                  </div>
                </div>

                {/* Accepted Offer Preview */}
                {req.acceptedOffer && (
                  <div className="border-t border-slate-100 dark:border-slate-700 pt-4">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-3 uppercase tracking-wide">Fulfillment Details</h4>
                    <div className="flex items-center justify-between bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-200 dark:bg-emerald-800 rounded-full flex items-center justify-center text-emerald-800 dark:text-emerald-200 font-bold">
                          {req.acceptedOffer.providerName.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white">{req.acceptedOffer.providerName}</p>
                          <p className="text-xs text-slate-600 dark:text-slate-300">{req.acceptedOffer.providerType}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{req.acceptedOffer.priceEstimate}</p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-500">Agreed Price</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {requestToCancel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fade-in">
             <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-fade-in-up border border-slate-200 dark:border-slate-700">
                <div className="p-6 text-center">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Cancel Request?</h3>
                    <p className="text-slate-600 dark:text-slate-300 mb-6">
                        Are you sure you want to cancel "<span className="font-semibold">{requestToCancel.title}</span>"? This action cannot be undone.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => setRequestToCancel(null)}
                            className="px-5 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                        >
                            Keep Request
                        </button>
                        <button
                            onClick={confirmCancelRequest}
                            className="px-5 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md"
                        >
                            Yes, Cancel it
                        </button>
                    </div>
                </div>
             </div>
        </div>
      )}

      {/* Manage Request Modal */}
      {isManageModalOpen && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center sticky top-0 bg-white dark:bg-slate-800 z-10">
              <h3 className="font-bold text-lg text-slate-900 dark:text-white">Manage Request</h3>
              <button onClick={() => setIsManageModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Status Section */}
              <section>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Status & Progress</h4>
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                   <div>
                     <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Current Status</p>
                     <p className={`font-bold text-lg ${
                        selectedRequest.status === 'OPEN' ? 'text-indigo-600 dark:text-indigo-400' :
                        selectedRequest.status === 'IN_PROGRESS' ? 'text-blue-600 dark:text-blue-400' :
                        selectedRequest.status === 'COMPLETED' ? 'text-emerald-600 dark:text-emerald-400' : 
                        selectedRequest.status === 'CANCELLED' ? 'text-red-600 dark:text-red-400' : 'text-slate-600'
                     }`}>
                       {selectedRequest.status.replace('_', ' ')}
                     </p>
                   </div>
                   {selectedRequest.status === 'IN_PROGRESS' && (
                     <button 
                       onClick={handleMarkCompleted}
                       className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-md"
                     >
                       Mark as Completed
                     </button>
                   )}
                   {selectedRequest.status === 'OPEN' && (
                     <span className="text-xs text-slate-500 dark:text-slate-400 italic">Accept an offer to start progress</span>
                   )}
                </div>
              </section>

              {/* Accepted Offer Details */}
              {selectedRequest.acceptedOffer && (
                <section>
                   <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Provider Info</h4>
                   <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
                      <div className="flex justify-between items-start">
                         <div>
                            <h5 className="font-bold text-lg text-slate-800 dark:text-white">{selectedRequest.acceptedOffer.offerTitle}</h5>
                            <p className="text-slate-600 dark:text-slate-300 text-sm mb-2">{selectedRequest.acceptedOffer.providerName}</p>
                         </div>
                         <span className="font-mono font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                            {selectedRequest.acceptedOffer.priceEstimate}
                         </span>
                      </div>
                      <div className="mt-4 flex gap-3">
                         <button className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">Message</button>
                         <button className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">Call</button>
                      </div>
                   </div>
                </section>
              )}

              {/* Completion & Feedback Section */}
              {(selectedRequest.status === 'COMPLETED' || selectedRequest.status === 'IN_PROGRESS') && (
                <>
                  <section>
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Documents & Evidence</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                       {selectedRequest.completionDocs?.map((doc, i) => (
                         <div key={i} className="aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                            <img src={doc} alt="Doc" className="w-full h-full object-cover" />
                         </div>
                       ))}
                       <button 
                         onClick={() => fileInputRef.current?.click()}
                         className="aspect-square bg-slate-50 dark:bg-slate-900/50 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors cursor-pointer"
                       >
                         {uploading ? (
                           <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                         ) : (
                           <>
                             <svg className="w-8 h-8 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                             <span className="text-xs font-bold">Upload</span>
                           </>
                         )}
                       </button>
                       <input 
                         type="file" 
                         ref={fileInputRef} 
                         onChange={handleFileUpload} 
                         className="hidden" 
                         multiple 
                         accept="image/*,.pdf" 
                       />
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500">Upload invoices, photos of work, or receipts.</p>
                  </section>

                  {selectedRequest.status === 'COMPLETED' && (
                    <section className="pt-6 border-t border-slate-100 dark:border-slate-700">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white uppercase mb-4">Feedback</h4>
                      {selectedRequest.feedback ? (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl border border-yellow-100 dark:border-yellow-900/30">
                           <div className="flex items-center gap-1 text-yellow-500 mb-2">
                             {[...Array(5)].map((_, i) => (
                               <svg key={i} className={`w-5 h-5 ${i < selectedRequest.feedback.rating ? 'fill-current' : 'text-yellow-200 dark:text-yellow-900 fill-current'}`} viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" /></svg>
                             ))}
                           </div>
                           <p className="text-slate-700 dark:text-slate-300 italic">"{selectedRequest.feedback.comment}"</p>
                           <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">Posted {new Date(selectedRequest.feedback.timestamp).toLocaleDateString()}</p>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmitFeedback} className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-xl border border-slate-200 dark:border-slate-700">
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Rate Provider</label>
                            <div className="flex gap-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setRating(star)}
                                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${rating >= star ? 'bg-yellow-400 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-300 dark:text-slate-500'}`}
                                >
                                  <svg className="w-6 h-6 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" /></svg>
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="mb-4">
                             <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Comment</label>
                             <textarea 
                               value={comment}
                               onChange={(e) => setComment(e.target.value)}
                               className="w-full p-3 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                               rows={3}
                               placeholder="How was the service?"
                               required
                             />
                          </div>
                          <button type="submit" className="w-full py-3 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                            Submit Review
                          </button>
                        </form>
                      )}
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;